/**
 * AteNoneBadge (Sawyer #1295, 10-29) — "Ate none at breakfast" on the desk's
 * rows the moment the FIRST meal is logged "Ate none".
 *
 * Sawyer (10-26): "a warning that a dog has 'ate none' … So, everyone knows
 * that dog specifically needs to be attended to." 10-29: raise it the first
 * time, not at end of day.
 *
 * Not a new alert system: it is the `CareStreakBadge` (same tokens, same
 * empty-bowl icon, same watch/severe levels) fed the server's ready-made
 * `ateNone` warning. The server computes the rule once (core
 * `computeAteNoneOn`); this file only parses and draws it.
 *
 * DESIGN-PHILOSOPHY §5 + the deploy-window rule: the frontend ships before
 * the server, so an older server sends no `ateNone`. Anything that is not a
 * well-formed warning renders NOTHING, never a crash and never a guess.
 */
import type { AteNoneWarning, MealSlot } from '../../lib/api.js';
import { CareStreakBadge } from './CareStreakBadge.js';

const MEALS: readonly string[] = ['breakfast', 'lunch', 'dinner', 'snack'];

/** The warning, or null for absent / null / malformed (older server). */
export function parseAteNone(value: unknown): AteNoneWarning | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as Record<string, unknown>;
  if (typeof v.label !== 'string' || v.label.trim().length === 0) return null;
  if (typeof v.days !== 'number' || !Number.isFinite(v.days) || v.days < 1) return null;
  if (!Array.isArray(v.meals) || v.meals.length === 0) return null;
  const meals = v.meals.filter((m): m is MealSlot => typeof m === 'string' && MEALS.includes(m));
  if (meals.length === 0) return null;
  return { meals, label: v.label, days: v.days };
}

/**
 * On a surface that ALSO shows the refusal streak (kennel roster, pet card,
 * care log): the streak is dropped when it names a meal the warning already
 * names, so a meal is never said twice. A streak for a different meal (last
 * night's dinner, not logged yet tonight) still shows.
 */
export function streakRepeatsAteNone(
  streakSlot: string | null | undefined,
  ateNone: AteNoneWarning | null,
): boolean {
  return ateNone !== null && typeof streakSlot === 'string' && ateNone.meals.includes(streakSlot as MealSlot);
}

export function AteNoneBadge({
  ateNone,
  dogName,
  size = 'sm',
  testId = 'ate-none-badge',
}: {
  /** The raw server field; parsed here so every caller is deploy-safe. */
  ateNone: unknown;
  dogName?: string;
  size?: 'sm' | 'md';
  testId?: string;
}): JSX.Element | null {
  const w = parseAteNone(ateNone);
  if (!w) return null;
  return <CareStreakBadge label={w.label} days={w.days} size={size} dogName={dogName} testId={testId} />;
}
